"use client";

/**
 * Paste a URL, we read the page and fill the form in.
 *
 * This is the one thing none of the tools currently ranking for this term do.
 * They all start you at an empty form and ask you to retype your own business
 * name, address, phone and opening hours off your own website.
 *
 * It is an accelerator, never a gate. A site behind a bot check cannot be read
 * from a server, and that is common enough that the failure path has to feel
 * ordinary rather than broken. So the message says what happened, says it is not
 * their fault, and points at the form that was always going to work anyway.
 */

import React, { useState } from "react";
import { AlertCircle, ArrowRight, FileSearch, Loader2 } from "lucide-react";
import type { PrefillCandidates } from "@/lib/schema-builder/extract";

export type PrefillResponse =
  | {
      ok: true;
      url: string;
      origin: string;
      platform?: string;
      existing: {
        types: string[];
        hasGraph: boolean;
        hasIds: boolean;
        isLinked: boolean;
        brokenBlocks: number;
        blockCount: number;
      };
      microdataFound: boolean;
      rdfaFound: boolean;
      candidates: PrefillCandidates;
    }
  | { ok: false; reason: string; url: string };

type Props = {
  onResult: (result: PrefillResponse) => void;
  initialUrl?: string;
  autoRun?: boolean;
  /** Overridden on the validator, where reading the page means checking it. */
  submitLabel?: string;
  busyLabel?: string;
  /**
   * Visible label above the field, stating what pasting a URL actually does.
   *
   * "Read my page" on a button says we will read it and stops there, which
   * leaves the obvious question unanswered: read it and then what? This is the
   * sentence that answers it, and it is a real label rather than help text so it
   * cannot be mistaken for small print.
   */
  prompt?: string;
  helper?: string;
};

export default function UrlPrefillBar({
  onResult,
  initialUrl = "",
  autoRun = false,
  submitLabel = "Read my page",
  busyLabel = "Reading your page",
  prompt = "Paste a page from your site and we fill the form in from what is on it",
  helper,
}: Props) {
  const [url, setUrl] = useState(initialUrl);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [hasRun, setHasRun] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  /**
   * Adopt whatever is already in the field once React takes it over.
   *
   * This is the first thing on the page, and the page is heavy, so there is a
   * real window where the field looks ready and is not yet wired up. Anything
   * typed, pasted or autofilled in that window lands in the DOM while state
   * stays empty, and because the submit button is disabled on empty state the
   * result is a filled-in box above a dead button, with nothing to tell you
   * that a second keystroke would fix it. Reading the value back at mount
   * closes the gap for pre-hydration input and browser autofill alike.
   */
  React.useEffect(() => {
    const present = inputRef.current?.value ?? "";
    if (present) setUrl((current) => current || present);
  }, []);

  const run = React.useCallback(
    async (value: string) => {
      const target = value.trim();
      if (loading) return;
      if (!target) {
        setNotice("Paste the address of a page on your site and we will read it.");
        inputRef.current?.focus();
        return;
      }

      setLoading(true);
      setNotice(null);

      try {
        const res = await fetch("/api/schema-prefill", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: target }),
        });
        const data = (await res.json()) as PrefillResponse & { error?: string };

        if (!res.ok) {
          setNotice(
            data.error ??
              "We could not read that page. Fill the fields in below and everything else works the same."
          );
          return;
        }

        if (!data.ok) {
          setNotice(data.reason);
          return;
        }

        setHasRun(true);
        onResult(data);
      } catch {
        setNotice(
          "We could not reach that page just now. Fill the fields in below and everything else works the same."
        );
      } finally {
        setLoading(false);
      }
    },
    [loading, onResult]
  );

  // Deep links from the blog and the checker can hand us a URL to scan.
  const autoRunRef = React.useRef(false);
  React.useEffect(() => {
    if (autoRun && initialUrl && !autoRunRef.current) {
      autoRunRef.current = true;
      void run(initialUrl);
    }
  }, [autoRun, initialUrl, run]);

  return (
    <div>
      <label
        htmlFor="schema-prefill-url"
        className="mb-2 block text-center font-sans text-[14px] font-medium text-nws-ink"
      >
        {prompt}
      </label>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          // The field, not the mirror of it. Belt and braces against the same
          // divergence the mount effect above repairs.
          void run(inputRef.current?.value ?? url);
        }}
        className="flex flex-col gap-2 sm:flex-row"
      >
        <div className="relative flex-1">
          <FileSearch
            className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-500"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            id="schema-prefill-url"
            type="text"
            inputMode="url"
            autoComplete="url"
            className="w-full rounded-full border border-nws-grey-medium bg-white py-3.5 pl-12 pr-4 font-sans text-[15px] text-nws-ink placeholder:text-gray-600 focus:border-nws-blue focus:outline-none focus:ring-2 focus:ring-nws-blue-light"
            placeholder="yoursite.com/services/plumbing"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={loading}
          />
        </div>
        {/*
         * Live unless a scan is already running.
         *
         * This used to disable on an empty `url`, which made the page's primary
         * control hostage to React state agreeing with the box next to it. Any
         * way of getting text in there without an input event - pasting before
         * hydration finishes, browser autofill, a password manager - left a
         * filled field above a dead button and no hint that one more keystroke
         * would wake it. An empty field is now caught on submit, where it can
         * say so, instead of by an affordance that cannot explain itself.
         */}
        <button
          type="submit"
          disabled={loading}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-black px-6 py-3.5 font-sans text-[15px] font-medium text-white transition-colors hover:bg-nws-ink disabled:cursor-not-allowed disabled:opacity-45"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {busyLabel}
            </>
          ) : (
            <>
              {hasRun ? "Try another page" : submitLabel}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </>
          )}
        </button>
      </form>

      {helper && (
        <p className="mt-3 text-center text-[13px] leading-relaxed text-gray-600">
          {helper}
        </p>
      )}

      {notice && (
        <div
          role="status"
          className="mt-3 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3.5"
        >
          <AlertCircle
            className="mt-0.5 h-[18px] w-[18px] shrink-0 text-amber-700"
            aria-hidden="true"
          />
          <p className="text-[13.5px] leading-relaxed text-nws-grey-text">{notice}</p>
        </div>
      )}
    </div>
  );
}
